import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X } from "@phosphor-icons/react";

/**
 * OverwriteWarningDialog — diszkrét megerősítő dialógus.
 *
 * Megjelenik, ha a felhasználó újragenerál egy posztot, miközben már van
 * egy kész poszt a panelen. Két opció:
 *   - "Mentés": save callback → új generálás
 *   - "Eldobás": csak új generálás (a régi felülíródik)
 *
 * Az "Emlékeztető kikapcsolása" pipa hatására a `disableForever` callback
 * fut le; a parent felel a backend-mentésért (`/api/user/preferences`).
 *
 * Props:
 *   open: boolean — vezérli a láthatóságot
 *   onSave: () => Promise<void> — Mentés gomb
 *   onDiscard: () => void — Eldobás gomb
 *   onClose: () => void — háttér-klikk vagy ESC
 *   onDisableForever: (checked: boolean) => void — pipa változás
 */
export default function OverwriteWarningDialog({
  open,
  onSave,
  onDiscard,
  onClose,
  onDisableForever,
}) {
  const [saving, setSaving] = useState(false);
  const [disableChecked, setDisableChecked] = useState(false);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (disableChecked) onDisableForever?.(true);
      await onSave?.();
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (saving) return;
    if (disableChecked) onDisableForever?.(true);
    onDiscard?.();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={onClose}
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(8, 8, 12, 0.72)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          data-testid="overwrite-warning-backdrop"
        >
          <motion.div
            key="card"
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 460, maxWidth: "calc(100vw - 48px)",
              background: "rgba(20, 20, 28, 0.96)",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              borderRadius: 16,
              padding: "32px 32px 24px",
              boxShadow: "0 24px 60px rgba(0, 0, 0, 0.4)",
              fontFamily: "inherit",
            }}
            data-testid="overwrite-warning-dialog"
          >
            <h2
              className="font-heading"
              style={{
                fontSize: 22, fontWeight: 600, color: "#e8e8f0",
                letterSpacing: "-0.012em", marginBottom: 14, lineHeight: 1.3,
              }}
            >
              Mentsük a korábbi posztodat,
              <br />
              vagy eldobhatjuk?
            </h2>
            <p style={{ fontSize: 13.5, color: "#9898ad", lineHeight: 1.55, marginBottom: 26 }}>
              Az új generálás felülírja a most látott posztot. Ha érdemes megtartani, mentsd el a Tudástárba.
            </p>

            {/* Action buttons */}
            <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                data-testid="overwrite-warning-save"
                style={{
                  flex: 1,
                  padding: "11px 16px",
                  borderRadius: 999,
                  background: "rgba(124, 108, 242, 0.18)",
                  border: "1px solid rgba(124, 108, 242, 0.4)",
                  color: "#cfc8ff",
                  fontSize: 13.5, fontWeight: 500,
                  cursor: saving ? "wait" : "pointer",
                  fontFamily: "inherit",
                  transition: "all 180ms ease",
                  opacity: saving ? 0.6 : 1,
                }}
              >
                {saving ? "Mentés…" : "Mentés"}
              </button>
              <button
                type="button"
                onClick={handleDiscard}
                disabled={saving}
                data-testid="overwrite-warning-discard"
                style={{
                  flex: 1,
                  padding: "11px 16px",
                  borderRadius: 999,
                  background: "rgba(255, 255, 255, 0.04)",
                  border: "1px solid rgba(255, 255, 255, 0.10)",
                  color: "#c3c3cc",
                  fontSize: 13.5, fontWeight: 500,
                  cursor: saving ? "wait" : "pointer",
                  fontFamily: "inherit",
                  transition: "all 180ms ease",
                  opacity: saving ? 0.6 : 1,
                }}
              >
                Eldobás
              </button>
            </div>

            {/* Disable-forever checkbox */}
            <label
              style={{
                display: "flex", alignItems: "center", gap: 8,
                cursor: "pointer",
                fontSize: 12.5, color: "#9898ad",
                userSelect: "none",
              }}
            >
              <span
                role="checkbox"
                aria-checked={disableChecked}
                tabIndex={0}
                onClick={() => setDisableChecked((v) => !v)}
                onKeyDown={(e) => {
                  if (e.key === " " || e.key === "Enter") {
                    e.preventDefault();
                    setDisableChecked((v) => !v);
                  }
                }}
                data-testid="overwrite-warning-disable-toggle"
                style={{
                  width: 16, height: 16, borderRadius: 4,
                  border: `1px solid ${disableChecked ? "rgba(124, 108, 242, 0.55)" : "rgba(255, 255, 255, 0.18)"}`,
                  background: disableChecked ? "rgba(124, 108, 242, 0.22)" : "transparent",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  transition: "all 160ms ease",
                  flexShrink: 0,
                }}
              >
                {disableChecked && <Check size={11} weight="bold" color="#cfc8ff" />}
              </span>
              <span onClick={() => setDisableChecked((v) => !v)}>
                Emlékeztető kikapcsolása
              </span>
            </label>

            {/* Close (X) */}
            <button
              type="button"
              onClick={onClose}
              data-testid="overwrite-warning-close"
              aria-label="Bezár"
              style={{
                position: "absolute",
                top: 18, right: 18,
                width: 28, height: 28,
                borderRadius: 999,
                background: "transparent",
                border: "1px solid rgba(255, 255, 255, 0.08)",
                color: "#9898ad",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <X size={14} />
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
